/**
 * W11 — BDT amount in words for money receipts (framework-free, domain layer).
 * Uses the South Asian grouping (crore / lakh / thousand / hundred) customary on Bangladeshi
 * receipts: 1,25,500.50 → "Taka One Lakh Twenty-Five Thousand Five Hundred and Fifty Paisa Only".
 * NaN / non-finite input degrades to "Taka Zero Only".
 */
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function belowHundred(n: number): string {
  if (n < 20) return ONES[n];
  const unit = n % 10;
  return unit ? `${TENS[Math.floor(n / 10)]}-${ONES[unit]}` : TENS[Math.floor(n / 10)];
}

function belowThousand(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundreds) parts.push(`${ONES[hundreds]} Hundred`);
  if (rest) parts.push(belowHundred(rest));
  return parts.join(' ');
}

function integerToWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 10_000_000);
  let rest = n % 10_000_000;
  // Above 99 crore the crore count itself is spelled recursively ("One Hundred Crore").
  if (crore) parts.push(`${integerToWords(crore)} Crore`);
  const lakh = Math.floor(rest / 100_000);
  rest %= 100_000;
  if (lakh) parts.push(`${belowHundred(lakh)} Lakh`);
  const thousand = Math.floor(rest / 1000);
  rest %= 1000;
  if (thousand) parts.push(`${belowHundred(thousand)} Thousand`);
  if (rest) parts.push(belowThousand(rest));
  return parts.join(' ');
}

export function amountInWords(amount: number): string {
  const safe = Number.isFinite(amount) ? Math.abs(amount) : 0;
  const totalPaisa = Math.round(safe * 100);
  const taka = Math.floor(totalPaisa / 100);
  const paisa = totalPaisa % 100;
  const body = paisa ? `${integerToWords(taka)} and ${belowHundred(paisa)} Paisa` : integerToWords(taka);
  return `Taka ${body} Only`;
}
